import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { sendNotification } from "../api/plan";
import { generateTokenByApiKey } from "../api/login";
import { SHOW_SNACKBAR } from "../utils/types";

const EnquiryPopup = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", requirement: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const validators = {
    name: (value) => {
      if (!value.trim()) return "Name is required";
      if (value.trim().length < 2) return "Name must be at least 2 characters";
      if (!/^[a-zA-Z\s]+$/.test(value.trim())) return "Name can only contain letters";
      return "";
    },
    email: (value) => {
      if (!value.trim()) return "Email is required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Enter a valid email address";
      return "";
    },
    mobile: (value) => {
      if (!value.trim()) return "Mobile number is required";
      if (!/^[6-9]\d{9}$/.test(value.trim())) return "Enter a valid 10-digit mobile number";
      return "";
    },
    requirement: (value) => {
      // Optional field — only guard against excessively long input
      if (value.length > 500) return "Please keep your query under 500 characters";
      return "";
    },
  };

  const validateField = (field, value) => validators[field](value);

  const handleChange = (field, rawValue) => {
    const value = field === "mobile" ? rawValue.replace(/\D/g, "").slice(0, 10) : rawValue;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const validateAll = () => {
    const newErrors = {};
    Object.keys(validators).forEach((field) => {
      newErrors[field] = validateField(field, formData[field]);
    });
    setErrors(newErrors);
    setTouched({ name: true, email: true, mobile: true });
    return Object.values(newErrors).every((err) => !err);
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", mobile: "", requirement: "" });
    setErrors({});
    setTouched({});
  };

  // Only the X button calls this — backdrop clicks are intentionally inert
  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validateAll()) return;

    setIsSubmitting(true);

    const emailBody = `
      <h3>New Enquiry Received</h3>
      <p><strong>Name:</strong> ${formData.name}</p>
      <p><strong>Email:</strong> ${formData.email}</p>
      <p><strong>Mobile:</strong> +91 ${formData.mobile}</p>
      <p><strong>Requirement:</strong> ${formData.requirement.trim() ? formData.requirement : "—"}</p>
    `;

    const request = {
      notificationType: "EMAIL",
      from: process.env.REACT_APP_SENDER_EMAIL,
      subject: "New Enquiry Request",
      toUsers: {
        [process.env.REACT_APP_TO_EMAIL]: {
          email: process.env.REACT_APP_TO_EMAIL,
        },
      },
      body: emailBody,
    };

    dispatch(
      generateTokenByApiKey(
        {},
        "zealthix",
        {
          "x-tenant-id": process.env.REACT_APP_TENANT_ID,
          "X-Api-Key": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
        (t) => {
          if (t?.access_token) {
            dispatch(
              sendNotification(request, t.access_token, () => {
                setIsSubmitting(false);
                resetForm();
                dispatch({
                  type: SHOW_SNACKBAR,
                  payload: {
                    type: "success",
                    message: "Enquiry sent successfully. We'll get back to you soon!",
                    open: true,
                  },
                });
                onClose();
              })
            );
          } else {
            setIsSubmitting(false);
            dispatch({
              type: SHOW_SNACKBAR,
              payload: {
                type: "error",
                message: "Something went wrong. Please try again.",
                open: true,
              },
            });
          }
        }
      )
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-[#021935]/60 backdrop-blur-sm">
      <div className="relative w-full max-w-[820px] max-h-[92vh] bg-white rounded-[20px] shadow-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2">
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close enquiry form"
          className="absolute top-[16px] right-[16px] z-10 size-[34px] flex items-center justify-center rounded-full bg-white shadow-md text-secondary hover:bg-[#1F87C9] hover:text-white duration-300"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>

        {/* Left: form */}
        <div className="order-2 md:order-1 px-[26px] sm:px-[40px] pt-[35px] pb-[35px] overflow-y-auto max-h-[92vh]">
          <span className="capitalize font-semibold text-base text-primary font-sora pb-[6px] block">
            # Get In Touch
          </span>
          <h3 className="font-bold text-[26px] sm:text-[28px] text-secondary font-sora leading-[1.2]">
            Enquire Now
          </h3>
          <p className="text-gary text-sm font-normal pt-[8px] pb-[28px]">
            Share your details and our team will reach out to you shortly.
          </p>

          <form onSubmit={onSubmit} noValidate className="space-y-[20px]">
            {/* Name */}
            <div>
              <label htmlFor="enquiry-name" className="block text-sm font-medium text-secondary font-urbanist mb-[8px]">
                Full Name
              </label>
              <input
                id="enquiry-name"
                type="text"
                placeholder="Enter your name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onBlur={() => handleBlur("name")}
                className={`w-full text-base px-[16px] py-[12px] rounded-[10px] border bg-white focus:outline-none focus:ring-2 duration-300 ${
                  touched.name && errors.name
                    ? "border-red-400 focus:ring-red-100"
                    : "border-[#D8D8D8] focus:border-[#1F87C9] focus:ring-[#1f87c91a]"
                }`}
              />
              {touched.name && errors.name && (
                <p className="text-red-500 text-xs font-medium mt-[6px]">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="enquiry-email" className="block text-sm font-medium text-secondary font-urbanist mb-[8px]">
                Email Address
              </label>
              <input
                id="enquiry-email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                className={`w-full text-base px-[16px] py-[12px] rounded-[10px] border bg-white focus:outline-none focus:ring-2 duration-300 ${
                  touched.email && errors.email
                    ? "border-red-400 focus:ring-red-100"
                    : "border-[#D8D8D8] focus:border-[#1F87C9] focus:ring-[#1f87c91a]"
                }`}
              />
              {touched.email && errors.email && (
                <p className="text-red-500 text-xs font-medium mt-[6px]">{errors.email}</p>
              )}
            </div>

            {/* Mobile */}
            <div>
              <label htmlFor="enquiry-mobile" className="block text-sm font-medium text-secondary font-urbanist mb-[8px]">
                Mobile Number
              </label>
              <div
                className={`flex items-center rounded-[10px] border bg-white focus-within:ring-2 duration-300 ${
                  touched.mobile && errors.mobile
                    ? "border-red-400 focus-within:ring-red-100"
                    : "border-[#D8D8D8] focus-within:border-[#1F87C9] focus-within:ring-[#1f87c91a]"
                }`}
              >
                <span className="px-[16px] text-base text-gary border-r border-[#D8D8D8] font-medium select-none">
                  +91
                </span>
                <input
                  id="enquiry-mobile"
                  type="tel"
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={formData.mobile}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                  onBlur={() => handleBlur("mobile")}
                  className="w-full text-base px-[16px] py-[12px] bg-transparent focus:outline-none"
                />
              </div>
              {touched.mobile && errors.mobile && (
                <p className="text-red-500 text-xs font-medium mt-[6px]">{errors.mobile}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-[8px]">
                <label htmlFor="enquiry-requirement" className="text-sm font-medium text-secondary font-urbanist">
                  Your Requirement <span className="text-gary font-normal">(optional)</span>
                </label>
                <span className="text-xs text-gary">{formData.requirement.length}/500</span>
              </div>
              <textarea
                id="enquiry-requirement"
                rows={3}
                maxLength={500}
                placeholder="Tell us briefly what you're looking for..."
                value={formData.requirement}
                onChange={(e) => handleChange("requirement", e.target.value)}
                onBlur={() => handleBlur("requirement")}
                className={`w-full text-base px-[16px] py-[12px] rounded-[10px] border bg-white focus:outline-none focus:ring-2 duration-300 resize-none ${
                  touched.requirement && errors.requirement
                    ? "border-red-400 focus:ring-red-100"
                    : "border-[#D8D8D8] focus:border-[#1F87C9] focus:ring-[#1f87c91a]"
                }`}
              />
              {touched.requirement && errors.requirement && (
                <p className="text-red-500 text-xs font-medium mt-[6px]">{errors.requirement}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#1F87C9] text-white font-semibold text-base py-[14px] rounded-[10px] hover:bg-[#021935] duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-[8px]"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin size-[18px]" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Sending...
                </>
              ) : (
                "Submit Enquiry"
              )}
            </button>
          </form>
        </div>

        <div className="order-1 md:order-2 hidden md:block relative h-full min-h-[420px]">
          <img
            src="/assets/img/enquiry-card.png"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

export default EnquiryPopup;